class FaqSection extends HTMLElement {
  #observer;
  #links;
  #headings;
  #pinnedId = null;
  #anchorScrollPending = false;
  #anchorScrollFallbackTimer;
  #onScroll;
  #onScrollEnd;
  #onNavClick;
  #onHashChange;

  connectedCallback() {
    this.#links = [...this.querySelectorAll('[data-navigation-button]')];
    this.#headings = [...this.querySelectorAll('[data-navigation-heading]')];

    if (!this.#headings.length) return;

    this.#onScroll = () => {
      if (this.#anchorScrollPending) return;
      if (this.#pinnedId === null) return;
      this.#pinnedId = null;
    };

    this.#onScrollEnd = () => {
      clearTimeout(this.#anchorScrollFallbackTimer);
      this.#anchorScrollPending = false;
    };

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('scrollend', this.#onScrollEnd);

    this.#onHashChange = () => {
      const id = location.hash.slice(1);
      if (id) {
        this.#pinnedId = id;
        this.#setActive(id);
        this.#beginAnchorScrollTracking();
      } else {
        this.#pinnedId = null;
      }
    };

    window.addEventListener('hashchange', this.#onHashChange);
    this.#initObserver();

    if (location.hash) {
      this.#pinnedId = location.hash.slice(1);
      this.#setActive(location.hash.slice(1));
      this.#beginAnchorScrollTracking();
    }
  }

  disconnectedCallback() {
    clearTimeout(this.#anchorScrollFallbackTimer);
    this.#observer?.disconnect();
    this.#links?.forEach((link) => link.removeEventListener('click', this.#onNavClick));
    window.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('scrollend', this.#onScrollEnd);
    window.removeEventListener('hashchange', this.#onHashChange);
  }

  #beginAnchorScrollTracking() {
    clearTimeout(this.#anchorScrollFallbackTimer);
    this.#anchorScrollPending = true;
    this.#anchorScrollFallbackTimer = setTimeout(() => {
      this.#anchorScrollPending = false;
    }, 1500);
  }

  #initObserver() {
    const offset = this.#headerOffset();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (this.#pinnedId !== null) return;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.#setActive(entry.target.id);
        });
      },
      {
        rootMargin: `-${offset}px 0px -60% 0px`,
      }
    );

    this.#headings.forEach((h) => this.#observer.observe(h));
  }

  #headerOffset() {
    return (
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) ||
      93
    );
  }

  #setActive(id) {
    this.#links.forEach((link) => {
      const active = link.hash === `#${id}`;

      link.classList.toggle('button--black', active);
      link.classList.toggle('button--gray', !active);

      active ? link.setAttribute('aria-current', 'location') : link.removeAttribute('aria-current');
    });
  }
}

customElements.define('faq-section', FaqSection);
